import { inject } from '@angular/core';
import { RedirectCommand, ResolveFn, Router } from '@angular/router';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import {
  SessionStatusEnum,
  SessionTypeEnum,
} from 'src/app/@data-services/event-agenda/event-agenda.data-model';
import { EventAgendaDataService } from 'src/app/@data-services/event-agenda/event-agenda.data-service';
import { SessionInsightsDataService } from 'src/app/@data-services/session-insights/session-insights-agenda.data-service';
import { EventConfigStateService } from 'src/app/@store/event-config-state.service';

const POST_LIVE_STATUSES: ReadonlyArray<string> = [
  SessionStatusEnum.UnderReview,
  SessionStatusEnum.ReviewComplete,
  SessionStatusEnum.ProcessingInsights,
];

const isPostLive = (status: string | undefined | null): boolean =>
  !!status && POST_LIVE_STATUSES.includes(status);

// TODO: @later Upgrade checklist item: Update usages of Resolve interface to include RedirectCommand in its return type.
export const determineSessionStatusResolver: ResolveFn<boolean> = (
  route,
  _state
) => {
  if (route.queryParamMap.get('internal')) {
    return true;
  }

  // Start downloading the page code now instead of after this check.
  void import(
    'src/app/@pages/session-insights/session-insights.component'
  ).catch(() => undefined);

  const router = inject(Router);
  const eventAgendaDataService = inject(EventAgendaDataService);
  const sessionInsightsDataService = inject(SessionInsightsDataService);
  const eventConfigStateService = inject(EventConfigStateService);

  const isPrimaryScreenParam = route.queryParamMap.get('isPrimaryScreen');
  if (isPrimaryScreenParam !== null) {
    eventConfigStateService.setPrimaryScreen(isPrimaryScreenParam === 'true');
  }

  const event = eventConfigStateService.$eventIdentifier();
  const sessionId = route.paramMap.get('sessionId') ?? '';

  const redirectToDebrief = () =>
    new RedirectCommand(
      // Carry `internal` so the debrief resolver's escape hatch fires and
      // it can't bounce us straight back to live. Without it, the two
      // resolvers ping-pong (skipLocationChange SPA redirects) while a
      // session's status is in flux during a live→post-live switch —
      // re-firing getEventDetails each hop, never rendering the component,
      // and pegging the (shared) renderer thread. `merge` keeps existing
      // params like isPrimaryScreen / language.
      router.createUrlTree(['session', sessionId, 'debrief'], {
        queryParams: { internal: '1' },
        queryParamsHandling: 'merge',
      }),
      { skipLocationChange: true }
    );

  // Full-agenda check, kept for the rare post-live case (breakouts can keep a primary live).
  const checkAgenda = (): Observable<boolean | RedirectCommand> =>
    eventAgendaDataService.getEventDetails({ event }).pipe(
      map((response) => {
        const selectedSession = response.data.find(
          (e) => e.PrimarySessionId === sessionId
        );
        return isPostLive(selectedSession?.Status) ? redirectToDebrief() : true;
      })
    );

  // Fast path: fetch only this session; the page reuses this same response.
  return sessionInsightsDataService
    .prefetchSessionDetails({ event, sessionId })
    .pipe(
      catchError(() => {
        sessionInsightsDataService.clearPrefetchedSessionDetails({
          event,
          sessionId,
        });
        return of(null);
      }),
      switchMap((response) => {
        const session = response?.data;
        if (session && !isPostLive(session.Status)) {
          return of(true);
        }
        // Only a primary session can be kept live by a breakout, so any other
        // finished session can go to the debrief without the agenda.
        if (
          session &&
          session.PrimarySessionId === sessionId &&
          session.Type !== SessionTypeEnum.PrimarySession &&
          session.Type !== SessionTypeEnum.BreakoutSession
        ) {
          return of(redirectToDebrief());
        }
        return checkAgenda();
      })
    );
};
